import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { agoIso, duration, fmtClockPrecise, fmtTime } from "../format";
import { navigate, useRoute } from "../route";
import { CopyLink } from "./CopyLink";
import type {
  LiveSessionEvent,
  LiveSessionSummary,
  SessionEvaluation,
} from "../types";

/**
 * The durable timeline of one live session.
 *
 * Answers the question the Users tab can't: what actually happened in a Zoom
 * call, a day or a month later. A live session's own state (`live_turns`,
 * `research_requests`, `pending_insights`) dies with its 30-minute Redis TTL,
 * and audio turns never reach the `conversations` record at all — so without
 * this, a real meeting leaves behind a transcript at best, and nothing about
 * the background work that fed the agent its context.
 *
 * Two services write here. The **avatar** lane is what the student heard; the
 * **engine** lane is what steered it. They are interleaved by wall clock, but
 * the view makes no causality claim across lanes — where that matters, follow
 * `turn_id` / `lookup_id`, which both lanes carry.
 */

/** Event families, for the filter strip. Order matters: first match wins. */
const FILTERS = [
  { key: "all", label: "All", match: () => true },
  {
    key: "turns",
    label: "Turns",
    match: (e: LiveSessionEvent) => e.type.startsWith("turn."),
  },
  {
    key: "async",
    label: "Async context",
    match: (e: LiveSessionEvent) =>
      /^(slow_thinking|research|insight|oob|skill|enrich)\./.test(e.type),
  },
  {
    key: "context",
    label: "Context",
    match: (e: LiveSessionEvent) => e.type.startsWith("context."),
  },
  {
    key: "direction",
    label: "Direction",
    match: (e: LiveSessionEvent) => e.type.startsWith("direction."),
  },
  {
    key: "problems",
    label: "Problems",
    match: (e: LiveSessionEvent) =>
      e.severity === "error" || e.severity === "warning",
  },
] as const;

type FilterKey = (typeof FILTERS)[number]["key"];

/** Keys rendered as the event's body; everything else becomes a metadata chip. */
const BODY_KEYS = [
  "text",
  "content",
  "director_cue",
  "insight",
  "question",
  "user_message",
];

/** How often to re-poll a session that is still running. */
const POLL_MS = 3000;

function bodyOf(event: LiveSessionEvent): string {
  const data = event.data || {};
  for (const key of BODY_KEYS) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
}

/** Context payloads are rendered by the panel above, never inline: a system
 *  instruction is tens of KB and would bury the timeline it explains. */
const CONTEXT_KEYS = ["system_instruction", "memory_facts", "user_profile"];

/** Rendered as a badge beside the event type, so not repeated in the meta strip. */
const BADGE_KEYS = ["commit_kind"];

/** The non-body fields, as a compact `key=value` strip. */
function metaOf(event: LiveSessionEvent): string {
  const data = event.data || {};
  return Object.entries(data)
    .filter(
      ([k, v]) =>
        !BODY_KEYS.includes(k) &&
        !CONTEXT_KEYS.includes(k) &&
        !BADGE_KEYS.includes(k) &&
        v !== "" &&
        v != null,
    )
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join("  ");
}

/** One line standing in for a context payload, pointing at the panel. */
function contextSummary(event: LiveSessionEvent): string {
  const data = event.data || {};
  if (typeof data.system_instruction === "string") {
    const kb = (data.system_instruction.length / 1024).toFixed(1);
    return `system instruction · ${kb} KB — see “What the agent knew” above`;
  }
  if (Array.isArray(data.memory_facts)) {
    return `${data.memory_facts.length} memory facts — see “What the agent knew” above`;
  }
  if (data.user_profile) return "user profile — see “What the agent knew” above";
  return "";
}

function severityClass(event: LiveSessionEvent): string {
  if (event.severity === "error") return "row-error";
  if (event.severity === "warning") return "row-warn";
  return "";
}

/**
 * Identity of one event.
 *
 * `seq` alone is NOT unique: it restarts at 1 in every writer process, and two
 * processes can write one session (a rolling deploy overlapping a live call, a
 * webhook landing on another instance). `proc` is what separates them.
 */
function eventKey(e: LiveSessionEvent): string {
  return `${e.source}:${e.proc ?? ""}:${e.seq}`;
}

/** Latest timestamp seen — where the incremental poll resumes from. */
function latestTs(events: LiveSessionEvent[]): string {
  let out = "";
  for (const e of events) if (e.ts > out) out = e.ts;
  return out;
}

function statusBadge(session: LiveSessionSummary) {
  const status = session.status || "active";
  if (status === "active") return <span className="badge">live</span>;
  if (status === "abandoned")
    return (
      <span className="badge warn" title={session.end_reason}>
        {session.end_reason || "abandoned"}
      </span>
    );
  return <span className="badge">{session.end_reason || "ended"}</span>;
}

/**
 * The post-session verdict as a coloured badge. Green = ran to completion,
 * red = a technical failure, amber = we could not attribute the ending
 * (undetermined), plain = a user drop-off. A trailing "?" flags a completion
 * whose ending actor is an inference, not evidence (`actor_verified=false`), so
 * a guess never reads as a fact. Hover for the reasoning and how it was judged.
 */
function evalBadge(ev?: SessionEvaluation) {
  if (!ev?.category) return <span className="muted small">—</span>;
  const tech = ev.technical_issue?.category || "technical";
  let cls = "badge";
  let label = "unclassified";
  // Lead with the primary outcome (Complete / Incomplete), then the reason —
  // mirrors the taxonomy: [Complete | Avatar Ending], [Incomplete | User Drop-off],
  // [Incomplete | User Ending] (chose to leave mid-session),
  // [Incomplete | <technical>], [Incomplete | Undetermined].
  switch (ev.category) {
    case "complete_avatar_ending":
      cls = "badge ok"; label = "Complete · Avatar ending"; break;
    case "complete_user_ending":
      cls = "badge ok"; label = "Complete · User ending"; break;
    case "incomplete_user_dropoff":
      cls = "badge"; label = "Incomplete · User drop-off"; break;
    case "incomplete_user_ending":
      cls = "badge"; label = "Incomplete · User ending"; break;
    case "incomplete_technical":
      cls = "badge bad"; label = `Incomplete · ${tech}`; break;
    case "incomplete_undetermined":
      cls = "badge warn"; label = "Incomplete · Undetermined"; break;
    default:
      cls = "badge"; label = "Unclassified";
  }
  // A completion/drop-off whose actor we only inferred is flagged, so a guess is
  // never mistaken for a fact. Undetermined already says "unknown", so skip it.
  const inferred =
    ev.actor_verified === false && ev.category !== "incomplete_undetermined";
  const title = [
    ev.reasoning,
    ev.method ? `judged via ${ev.method}${ev.rule_id ? " · " + ev.rule_id : ""}` : "",
    inferred ? "ending actor is inferred (unverified)" : "",
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <span className={cls} title={title}>
      {label}
      {inferred ? " ?" : ""}
    </span>
  );
}

// ── the context panel ──────────────────────────────────────────────────

/**
 * What the agent knew, lifted out of the chronological table.
 *
 * A system instruction is tens of KB; inlined at its timestamp it would bury
 * the timeline it is supposed to explain. There are two of them and the
 * difference matters: the ENGINE prompt drives cue selection and background
 * research, the AVATAR prompt is what shapes the voice the student actually
 * heard.
 */
function ContextPanel({ events }: { events: LiveSessionEvent[] }) {
  const [open, setOpen] = useState(false);

  const context = useMemo(() => {
    const prompts = events.filter((e) =>
      e.type === "context.system_instruction" ||
      e.type === "context.live_system_instruction",
    );
    const facts = events.filter((e) => e.type === "context.memory_facts").pop();
    const profile = events.filter((e) => e.type === "context.user_profile").pop();
    return { prompts, facts, profile };
  }, [events]);

  if (!context.prompts.length && !context.facts && !context.profile) return null;

  const factRows = (context.facts?.data?.memory_facts as
    | Record<string, unknown>[]
    | undefined) || [];
  const profile = context.profile?.data?.user_profile as
    | Record<string, unknown>
    | undefined;

  return (
    <div className="card">
      <header className="section-head">
        <h3>What the agent knew</h3>
        <button className="ghost small" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Show"}
        </button>
      </header>
      {!open && (
        <p className="muted small">
          {context.prompts.length} system instruction
          {context.prompts.length === 1 ? "" : "s"} · {factRows.length} memory
          fact{factRows.length === 1 ? "" : "s"}
          {context.prompts.length > 1 &&
            " · the prompt changed mid-session"}
        </p>
      )}
      {open && (
        <>
          {context.prompts.map((prompt, i) => (
            <div key={`${prompt.source}-${prompt.seq}-${i}`}>
              <p className="small">
                <span className="badge">{prompt.source}</span>{" "}
                {prompt.type === "context.live_system_instruction"
                  ? "spoken voice (Gemini Live)"
                  : "steering prompt"}
                <span className="muted">
                  {" "}
                  · {fmtClockPrecise(prompt.ts)}
                  {typeof prompt.data?.reason === "string"
                    ? ` · ${prompt.data.reason}`
                    : ""}
                  {typeof prompt.data?.connect_seq === "number"
                    ? ` · connect #${prompt.data.connect_seq}`
                    : ""}
                </span>
              </p>
              <div className="table-scroll">
                <pre className="small" style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                  {String(prompt.data?.system_instruction ?? "")}
                </pre>
              </div>
            </div>
          ))}

          {factRows.length > 0 && (
            <>
              <p className="small">Memory facts</p>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Scope</th>
                      <th>Goal</th>
                      <th>Fact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factRows.map((fact, i) => (
                      <tr key={String(fact.fact_id ?? i)}>
                        <td className="nowrap muted">{String(fact.scope ?? "")}</td>
                        <td className="nowrap muted">{String(fact.goal_id ?? "")}</td>
                        <td>{String(fact.text ?? "")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {profile && (
            <>
              <p className="small">User profile</p>
              <dl className="kv">
                {Object.entries(profile).flatMap(([k, v]) => [
                  <dt key={`${k}-t`}>{k}</dt>,
                  <dd key={`${k}-d`}>
                    {typeof v === "object" ? JSON.stringify(v) : String(v)}
                  </dd>,
                ])}
              </dl>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── the timeline ───────────────────────────────────────────────────────

/**
 * `initial` is the row from the list, when you arrived by clicking one. Opening
 * a shared link has no list behind it, so the id alone has to be enough — the
 * summary is fetched either way and this only saves a flash of empty header.
 */
function Timeline({
  msid,
  initial,
  onBack,
}: {
  msid: string;
  initial?: LiveSessionSummary;
  onBack: () => void;
}) {
  const [summary, setSummary] = useState<LiveSessionSummary>(
    initial ?? { meeting_session_id: msid },
  );
  const [events, setEvents] = useState<LiveSessionEvent[]>([]);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [truncated, setTruncated] = useState(false);
  // Unknown until the summary arrives on a deep link, so start off and let the
  // effect below turn it on if the session turns out to still be running.
  const [following, setFollowing] = useState(initial?.status === "active");
  const armedFollow = useRef(false);
  const cursor = useRef("");
  const inFlight = useRef(false);

  // Auto-follow a live session exactly once, so pausing it stays paused.
  useEffect(() => {
    if (armedFollow.current) return;
    if (summary.status === "active") {
      armedFollow.current = true;
      setFollowing(true);
    } else if (summary.status) {
      armedFollow.current = true;
    }
  }, [summary.status]);

  const load = useCallback(
    async (incremental = false) => {
      // A poll slower than the interval would otherwise re-issue the same
      // cursor and append the same events twice.
      if (inFlight.current) return;
      inFlight.current = true;
      if (!incremental) setLoading(true);
      setError("");
      try {
        if (!incremental) cursor.current = "";
        const [sess, page] = await Promise.allSettled([
          api.liveSession(msid),
          api.liveSessionEvents(msid, incremental ? cursor.current : ""),
        ]);
        if (sess.status === "fulfilled") setSummary(sess.value);
        if (page.status === "rejected") throw page.reason;

        const fresh = page.value.events;
        setTruncated(page.value.truncated);
        const newest = latestTs(fresh);
        if (newest > cursor.current) cursor.current = newest;
        setEvents((prev) => {
          if (!incremental) return fresh;
          // The server's cursor is inclusive, so the boundary millisecond comes
          // back on every poll — dedupe rather than trusting it to be exact.
          const seen = new Set(prev.map(eventKey));
          return [...prev, ...fresh.filter((e) => !seen.has(eventKey(e)))];
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        inFlight.current = false;
        setLoading(false);
      }
    },
    [msid],
  );

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Poll only while the call is actually running — the reason to open this page
   * mid-session is to watch it. Every other view here is manual-refresh, and an
   * ended session stays that way. Paused when the tab is hidden, and the read
   * is incremental (`after_seq_*`), so a follower costs one small query per
   * tick rather than re-fetching the session.
   */
  useEffect(() => {
    if (!following || summary.status !== "active") return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void load(true);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [following, summary.status, load]);

  const shown = useMemo(() => {
    const rule = FILTERS.find((f) => f.key === filter) ?? FILTERS[0];
    const sorted = [...events].sort((a, b) =>
      a.ts !== b.ts
        ? a.ts.localeCompare(b.ts)
        : a.source !== b.source
          ? a.source.localeCompare(b.source)
          : (a.proc ?? "") !== (b.proc ?? "")
            ? (a.proc ?? "").localeCompare(b.proc ?? "")
            : a.seq - b.seq,
    );
    return sorted.filter(rule.match);
  }, [events, filter]);

  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const f of FILTERS) out[f.key] = events.filter(f.match).length;
    return out;
  }, [events]);

  const lanes = useMemo(() => {
    const out: Record<string, number> = {};
    for (const e of events) out[e.source] = (out[e.source] || 0) + 1;
    return out;
  }, [events]);

  const isLesson = /lesson|lecture/.test(summary.session_type || "");

  return (
    <section>
      <header className="section-head">
        <h2>
          <button className="ghost small" onClick={onBack}>
            ← Sessions
          </button>{" "}
          <code>{msid}</code>
        </h2>
        <span>
          <CopyLink route={{ tab: "sessions", msid }} />{" "}
          {summary.status === "active" && (
            <button
              className="ghost small"
              onClick={() => setFollowing((v) => !v)}
            >
              {following ? "Following · pause" : "Follow"}
            </button>
          )}{" "}
          <button
            className="ghost small"
            disabled={loading}
            onClick={() => void load()}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </span>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <div className="card">
        <h3>
          {summary.session_type || "session"} {statusBadge(summary)}
          {summary.synthetic_msid && (
            <span
              className="badge warn"
              title="No meeting id — this id was derived from the session start time (web console or a local driver)."
            >
              synthetic id
            </span>
          )}
        </h3>
        <dl className="kv">
          <dt>User</dt>
          <dd>
            <code>{summary.user_id || "—"}</code>
          </dd>
          <dt>Goal</dt>
          <dd>{summary.goal_id || "—"}</dd>
          <dt>Started</dt>
          <dd>
            {fmtTime(summary.started_at)}{" "}
            <span className="muted">· {agoIso(summary.started_at)}</span>
          </dd>
          <dt>Duration</dt>
          <dd>
            {duration(summary.started_at, summary.ended_at) ||
              (summary.status === "active" ? "in progress" : "—")}
          </dd>
          <dt>Lanes</dt>
          <dd>
            {Object.entries(lanes).length
              ? Object.entries(lanes)
                  .map(([k, v]) => `${k} ${v}`)
                  .join(" · ")
              : "—"}
          </dd>
          <dt>Env</dt>
          <dd>
            {summary.env || "—"}
            {summary.release && (
              <span className="muted small"> · {summary.release}</span>
            )}
          </dd>
          <dt>Evaluation</dt>
          <dd>
            {evalBadge(summary.evaluation)}
            {summary.evaluation?.reasoning && (
              <div className="muted small">{summary.evaluation.reasoning}</div>
            )}
          </dd>
        </dl>
        {summary.counters && Object.keys(summary.counters).length > 0 && (
          <p className="small muted">
            {Object.entries(summary.counters)
              .filter(([, v]) => v)
              .map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`)
              .join(" · ")}
          </p>
        )}
      </div>

      <ContextPanel events={events} />

      {isLesson && (lanes.engine ?? 0) < 5 && (
        <p className="muted small">
          Lecture walks bypass engine turn-gating by design — the avatar lane is
          the record for this session type, and a thin engine lane is expected.
        </p>
      )}
      {truncated && (
        <p className="error" role="alert">
          This session exceeded the 5000-event cap — the timeline below is the
          earliest portion, not the whole call.
        </p>
      )}

      <div className="tabs">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={filter === f.key ? "tab active" : "tab"}
            onClick={() => setFilter(f.key)}
          >
            {f.label} <span className="count">{counts[f.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {!shown.length && !loading ? (
        <p className="muted empty">
          {events.length
            ? "Nothing in this filter."
            : "No events logged for this session."}
        </p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Lane</th>
                <th>Event</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => (
                <tr key={eventKey(e)} className={severityClass(e)}>
                  <td className="nowrap muted small">{fmtClockPrecise(e.ts)}</td>
                  <td className="nowrap">
                    <span className="badge">{e.source}</span>
                  </td>
                  <td className="nowrap">
                    <code className="small">{e.type}</code>
                    {/* One utterance can reach the engine on more than one
                        forward — an early `provisional` pass, then `research`
                        carrying the complete question. Without the kind, two
                        turn.user rows with identical text look like a bug. */}
                    {typeof e.data?.commit_kind === "string" &&
                      e.data.commit_kind && (
                        <span className="badge"> {e.data.commit_kind}</span>
                      )}
                    {e.turn_id && (
                      <div className="muted small">{e.turn_id}</div>
                    )}
                  </td>
                  <td>
                    {e.type.startsWith("context.") ? (
                      <div className="muted">{contextSummary(e)}</div>
                    ) : (
                      bodyOf(e) && (
                        <div style={{ whiteSpace: "pre-wrap" }}>{bodyOf(e)}</div>
                      )
                    )}
                    {metaOf(e) && (
                      <div className="muted small">{metaOf(e)}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted small">
        {shown.length} of {events.length} events
        {summary.status === "active" && following && " · following"}
      </p>
    </section>
  );
}

// ── the list ───────────────────────────────────────────────────────────

export function LiveSessions() {
  const [rows, setRows] = useState<LiveSessionSummary[]>([]);
  const [cursor, setCursor] = useState("");
  const [query, setQuery] = useState("");
  const route = useRoute();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (next = "") => {
    setLoading(true);
    setError("");
    try {
      const page = await api.listLiveSessions(next);
      setRows((prev) => (next ? [...prev, ...page.items] : page.items));
      setCursor(page.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Client-side, deliberately: a server-side filter alongside the `started_at`
  // sort would need a composite index this repo provisions by hand. Same call
  // the Waitlist and Allowlist views make.
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [
        r.meeting_session_id,
        r.user_id,
        r.session_type,
        r.end_reason,
        r.evaluation?.category,
        r.evaluation?.technical_issue?.category,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [rows, query]);

  if (route.msid) {
    return (
      <Timeline
        msid={route.msid}
        initial={rows.find((r) => r.meeting_session_id === route.msid)}
        onBack={() => navigate({ tab: "sessions" })}
      />
    );
  }

  return (
    <section>
      <header className="section-head">
        <h2>Live sessions</h2>
        <button
          className="ghost"
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </header>
      <p className="muted small">
        The durable record of each meeting — what was said, and the background
        work that fed the agent its context. Read-only, and every session opened
        is logged with your account.
      </p>

      <form className="add-admin" onSubmit={(e) => e.preventDefault()}>
        <input
          type="search"
          placeholder="Filter by session id, user, type or end reason"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </form>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {!shown.length && !loading ? (
        <p className="muted empty">
          {rows.length
            ? "No session matches that filter."
            : "No live sessions logged yet."}
        </p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Started</th>
                <th>User</th>
                <th>Type</th>
                <th>Duration</th>
                <th>Turns</th>
                <th>Insights</th>
                <th>Status</th>
                <th>Evaluation</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((s) => {
                const c = s.counters || {};
                const dropped = c.insights_dropped || 0;
                // Prefer the avatar's counts. Bean's speech is only observable
                // there — a lecture walk teaches autonomously, so the engine's
                // `bean_turns` reads 0 for a session where Bean spoke a dozen
                // times. `heard_turns` is the ASR's one-per-utterance commit;
                // the engine counts per FORWARD, which is a larger number.
                const heard = c.heard_turns ?? c.user_turns ?? 0;
                const spoken = c.spoken_turns ?? c.bean_turns ?? 0;
                return (
                  <tr
                    key={s.meeting_session_id}
                    onClick={() =>
                      navigate({ tab: "sessions", msid: s.meeting_session_id })
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <td className="nowrap">
                      {fmtTime(s.started_at)}
                      <div className="muted small">{s.meeting_session_id}</div>
                    </td>
                    <td className="nowrap">
                      <code className="small">{s.user_id || "—"}</code>
                    </td>
                    <td className="nowrap">{s.session_type || "—"}</td>
                    <td className="nowrap muted">
                      {duration(s.started_at, s.ended_at) ||
                        (s.status === "active" ? "in progress" : "—")}
                    </td>
                    <td className="nowrap muted" title="user heard / Bean spoken">
                      {heard}/{spoken}
                    </td>
                    <td className="nowrap">
                      <span className="muted">{c.insights_landed || 0}</span>
                      {dropped > 0 && (
                        <span className="badge warn"> {dropped} dropped</span>
                      )}
                    </td>
                    <td className="nowrap">{statusBadge(s)}</td>
                    <td className="nowrap">{evalBadge(s.evaluation)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted small">
        {shown.length} session{shown.length === 1 ? "" : "s"}
        {query && rows.length !== shown.length && ` of ${rows.length}`}
        {cursor && (
          <>
            {" · "}
            <button
              className="ghost small"
              disabled={loading}
              onClick={() => void load(cursor)}
            >
              Load more
            </button>
          </>
        )}
      </p>
    </section>
  );
}
